import { IS_WEB } from '@/shared/constants/utility';
import SentryService from '@/services/sentry/Sentry.service';
const bellChordSound = require('@/assets/sounds/bellChord.mp3');

class SoundNotificationService {
  private audio: HTMLAudioElement | null = null;
  private isInitialized = false;
  private userHasInteracted = false;

  constructor() {
    if (
      IS_WEB &&
      typeof window !== 'undefined' &&
      typeof document !== 'undefined'
    ) {
      this.initializeAudio();
      this.setupUserInteractionListener();
    }
  }

  private initializeAudio() {
    try {
      // Create audio element with the bell-chord sound
      this.audio = new Audio();
      this.audio.src = bellChordSound;
      this.audio.preload = 'auto';
      this.audio.volume = 0.7;

      // Handle audio loading events
      this.audio.addEventListener('canplaythrough', () => {
        this.isInitialized = true;
      });

      this.audio.addEventListener('error', (error) => {
        SentryService.captureError('Failed to load bell sound', {
          error,
          audioSrc: bellChordSound,
          readyState: this.audio?.readyState,
          networkState: this.audio?.networkState,
        });
      });
    } catch (error) {
      SentryService.captureError(
        'Failed to initialize audio for sound notifications',
        error,
      );
    }
  }

  private setupUserInteractionListener() {
    // Modern browsers require user interaction before playing audio
    // We'll track when the user has interacted with the page
    if (typeof document === 'undefined') {
      return;
    }

    const handleUserInteraction = () => {
      this.userHasInteracted = true;

      // Try to play a silent sound to "unlock" audio context
      if (this.audio && this.isInitialized) {
        const originalVolume = this.audio.volume;
        this.audio.volume = 0;
        this.audio
          .play()
          .then(() => {
            this.audio!.pause();
            this.audio!.currentTime = 0;
            this.audio!.volume = originalVolume;
          })
          .catch(() => {
            // Silently handle this - it's expected on some browsers
          });
      }

      // Remove listeners after first interaction
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };

    // Listen for various user interaction events
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, {
      once: true,
    });
  }

  /**
   * Play the match notification sound
   * This method handles browser autoplay restrictions gracefully
   */
  public async playMatchNotification(): Promise<boolean> {
    if (
      !IS_WEB ||
      typeof window === 'undefined' ||
      !this.audio ||
      !this.isInitialized
    ) {
      return false;
    }

    try {
      // Reset audio to beginning
      this.audio.currentTime = 0;

      // Attempt to play the sound
      await this.audio.play();

      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        // This is expected if user hasn't interacted with the page yet
        return false;
      } else {
        SentryService.captureError(
          'Failed to play match notification sound',
          error,
        );
        return false;
      }
    }
  }
}

// Export singleton instance
export default new SoundNotificationService();
